"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLinearClaimer = void 0;
var react_1 = require("react");
var web3_utils_1 = require("web3-utils");
var _abi_1 = require("../abi");
var useLinearClaimerContract_1 = require("../contracts/useLinearClaimerContract");
var _launchpadConstants_1 = require("../launchpadConstants");
var _shared_1 = require("../shared");
var utils_1 = require("../shared/utils");
var multicall_1 = require("../shared/utils/multicall");
var DEFAULT_VERSION = 'v1';
function useLinearClaimer(_a) {
    var token = _a.token, distribution = _a.distribution;
    var _b = (0, _shared_1.useWeb3Provider)(), targetChain = _b.targetChain, targetChainWeb3 = _b.targetChainWeb3, connectedChain = _b.connectedChain, switchToChain = _b.switchToChain;
    var account = (0, _shared_1.useUser)().account;
    var claimerAddress = distribution.claimerAddress, claimerVersion = distribution.claimerVersion, network = distribution.network;
    var symbol = distribution.symbol || token.symbol;
    var version = claimerVersion || DEFAULT_VERSION;
    var instance = (0, useLinearClaimerContract_1.useLinearClaimerContract)(claimerAddress, 
    // @ts-expect-error
    version);
    var _c = (0, react_1.useState)({
        userTotal: _launchpadConstants_1.ZERO_BN,
        userTotalClaimed: _launchpadConstants_1.ZERO_BN,
        userClaimable: _launchpadConstants_1.ZERO_BN,
    }), userStats = _c[0], setUserStats = _c[1];
    var _d = (0, react_1.useState)({
        total: _launchpadConstants_1.ZERO_BN,
        totalClaimed: _launchpadConstants_1.ZERO_BN,
        totalRemaining: _launchpadConstants_1.ZERO_BN,
    }), totalStats = _d[0], setTotalStats = _d[1];
    var _e = (0, react_1.useState)(__assign(__assign({}, distribution.config), { unitOfTime: distribution.config.unitOfTime || 1, initialPercent: distribution.config.initialPercent
            ? distribution.config.initialPercent * 10
            : distribution.config.initialPercent })), config = _e[0], setConfig = _e[1];
    var _f = (0, react_1.useState)(false), loaded = _f[0], setLoaded = _f[1];
    var _g = (0, react_1.useState)(false), paused = _g[0], setPaused = _g[1];
    var _h = (0, react_1.useState)(), isClaiming = _h[0], setIsClaiming = _h[1];
    var _j = (0, react_1.useState)(true), isEligible = _j[0], setIsEligible = _j[1];
    var sendOptions = {
        value: 0,
        from: account,
        gasPrice: targetChain.symbol === 'POLY' ? '420000000000' : undefined,
    };
    (0, react_1.useEffect)(function () {
        if (connectedChain.symbol === distribution.network &&
            targetChain.symbol !== connectedChain.symbol) {
            switchToChain(distribution.network);
        }
    }, [connectedChain.symbol, targetChain.symbol, distribution.network]);
    (0, react_1.useEffect)(function () {
        if (!targetChainWeb3 || !distribution.claimerAddress)
            return;
        var toNorm = function (v) { return (0, utils_1.normalizeDecimals)((0, web3_utils_1.toBN)(v), token.decimals); };
        var toInt = function (v) { return parseInt(v); };
        var methods = {
            getTotalStats: function (v) { return v.map(toNorm); },
            isPaused: function (v) { return !!v; },
            unitOfTime: toInt,
            initialPercent: toInt,
            initialUnlockDate: toInt,
            vestingStartDate: toInt,
            vestingDuration: toInt,
        };
        (0, multicall_1.multicallTransformed)({
            web3: targetChainWeb3,
            methods: methods,
            address: claimerAddress,
            abi: _abi_1.CONTRACTS.linearClaimer[version].abi,
            returnCachedOnError: true,
        }).then(function (_a) {
            var totalStats = _a[0], isPaused = _a[1], unitOfTime = _a[2], initialPercent = _a[3], initialUnlockDate = _a[4], vestingStartDate = _a[5], vestingDuration = _a[6];
            var total = totalStats[0], totalClaimed = totalStats[1], totalRemaining = totalStats[2];
            setTotalStats({ total: total, totalClaimed: totalClaimed, totalRemaining: totalRemaining });
            setPaused(isPaused);
            setConfig({
                unitOfTime: unitOfTime,
                initialUnlockDate: initialUnlockDate
                    ? new Date(initialUnlockDate * 1000).toISOString()
                    : '',
                initialPercent: initialPercent,
                vestingStartDate: new Date(vestingStartDate * 1000).toISOString(),
                vestingDuration: vestingDuration,
            });
        });
    }, [version, claimerAddress, targetChainWeb3, account, token.decimals]);
    // Refresh when new item becomes claimable
    // TODO: maybe also periodical refresh (but need indication that it loads)
    (0, react_1.useEffect)(function () {
        if (!instance || (!config.initialUnlockDate && !config.vestingStartDate))
            return;
        var nextTime = getNextFutureDate(config);
        if (nextTime) {
            var id_1 = setTimeout(refresh, nextTime - Date.now());
            return function () { return clearTimeout(id_1); };
        }
    }, [config.initialUnlockDate, config.vestingStartDate]);
    (0, react_1.useEffect)(function () {
        if (!instance || !account)
            return;
        refresh().finally(function () { return setLoaded(true); });
    }, [instance, account]);
    function refresh() {
        if (!account)
            return;
        var toNorm = function (v) { return (0, utils_1.normalizeDecimals)((0, web3_utils_1.toBN)(v), token.decimals); };
        var promises = [];
        if (version === 'v2' && account) {
            promises.push((0, utils_1.call)(instance, 'isAccountEligible', [account]).then(setIsEligible));
        }
        promises.push((0, utils_1.call)(instance, 'getAccountStats', [account])
            .then(function (_a) {
            var userTotal = _a[0], userTotalClaimed = _a[1], userClaimable = _a[2];
            setUserStats({
                userTotal: toNorm(userTotal),
                userTotalClaimed: toNorm(userTotalClaimed),
                userClaimable: toNorm(userClaimable),
            });
        })
            .catch(function (e) { return console.error('Failed to fetch claims', e); }));
        return Promise.all(promises);
    }
    function claimAll() {
        if (!account)
            return;
        setIsClaiming(-1);
        return instance.methods
            .claimAll(account)
            .send(sendOptions)
            .then(function () {
            refresh();
            return { message: 'Claimed all' };
        })
            .catch(function () {
            return {
                error: 'Failed to claim',
            };
        })
            .finally(function () { return setIsClaiming(undefined); });
    }
    return __assign(__assign(__assign(__assign({}, totalStats), { config: config, loaded: loaded, paused: paused, isClaiming: isClaiming, symbol: symbol, chain: network, address: claimerAddress }), userStats), { isEligible: isEligible, 
        // functions
        claimTokens: function () {
            throw new Error('Not supported');
        }, claimAll: claimAll });
}
exports.useLinearClaimer = useLinearClaimer;
function getNextFutureDate(config) {
    if (config.initialUnlockDate &&
        Date.parse(config.initialUnlockDate) > Date.now()) {
        return Date.parse(config.initialUnlockDate);
    }
    if (config.vestingStartDate &&
        Date.parse(config.initialUnlockDate) > Date.now()) {
        return Date.parse(config.vestingStartDate);
    }
    return 0;
}
